import { Box, Typography } from "@mui/material";
import { useQuery } from "convex/react";
import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useLocation } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import useWindowSize from "@/hooks/useWindowSize";
import { useAnalytics } from "@/hooks/useAnalytics";
import { trackClick } from "@/components/_helpers";
import { isConvexConfigured } from "@/lib/convex";
import { API_CACHE_TTL, fetchCachedGetApi } from "@/utils/apiCache";
import { isGatedArtistPath } from "@/utils/artistGate";
import { BREAKPOINT, getOptimizedImageUrl, parseURLParams } from "../_helpers";
import BaseModal from "../ui/BaseModal";
import { MAILING_POPUP_OPEN_EVENT, getPopupDismissedAt, getSubscribedEmail, rememberPopupDismissed, rememberPopupShownThisSession, wasPopupShownThisSession } from "./mailingStorage";
import SubscribeForm from "./SubscribeForm";
import { describeSubscribeOutcome } from "@/utils/mailingUnlock";
import { getRouteArtistSlug, releaseRouteFromPath } from "@/utils/routePaths";
import { fileCountLabel } from "@/utils/releaseAssets";

const DAY_MS = 24 * 60 * 60 * 1000;
const HIDDEN_ROOTS = new Set(["admin", "newsletter", "members", "branches", "merch"]);

interface ArtistLookup {
  artistData?: { name?: string; images?: Array<{ url: string }> };
}

function PackFan({ covers, artistImage }: { covers: string[]; artistImage?: string }) {
  const tiles = covers.length > 0 ? covers.slice(0, 3) : artistImage ? [artistImage] : [];
  if (tiles.length === 0) return null;
  const rotations = [-9, 3, 12];
  const offsets = [0, 18, 36];
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: { xs: "180px", md: "100%" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {tiles.map((url, index) => (
        <Box
          key={url}
          component="img"
          src={url}
          alt=""
          loading="lazy"
          sx={{
            position: "absolute",
            width: tiles.length === 1 ? "62%" : "44%",
            aspectRatio: covers.length > 0 ? "9 / 16" : "1 / 1",
            objectFit: "cover",
            borderRadius: "14px",
            boxShadow: "0 18px 40px rgba(0,0,0,0.45), 0 2px 0 rgba(255,255,255,0.08) inset",
            transform: `translateX(${(index - (tiles.length - 1) / 2) * offsets[1]}%) rotate(${rotations[index] ?? 0}deg)`,
            transformOrigin: "50% 120%",
            transition: "transform 600ms cubic-bezier(0.23, 1, 0.32, 1)",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
      ))}
    </Box>
  );
}

export function MailingListPopup() {
  const location = useLocation();
  const windowSize = useWindowSize();
  const { track } = useAnalytics();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const settings = useQuery(api.settings.getMailingPopup, isConvexConfigured ? {} : "skip");
  const latestPacks = useQuery(api.assets.listLatestPacks, isConvexConfigured ? { limit: 3 } : "skip");
  const releaseRoute = useMemo(() => releaseRouteFromPath(location.pathname), [location.pathname]);
  const downloadCounts = useQuery(
    api.assets.downloadCounts,
    isConvexConfigured ? (releaseRoute ? { artist: releaseRoute.artist, releaseName: releaseRoute.releaseName } : {}) : "skip",
  );
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<"form" | "done">("form");
  const [outcomeMessage, setOutcomeMessage] = useState<string | null>(null);
  const [outcomeConfirmed, setOutcomeConfirmed] = useState(false);
  const [artist, setArtist] = useState<{ name: string; image?: string } | null>(null);
  const shownRef = useRef(false);

  const parts = location.pathname.split("/").filter(Boolean);
  const artistSlug = useMemo(() => getRouteArtistSlug(parts), [location.pathname]);
  const hiddenRoute = HIDDEN_ROOTS.has(parts[0] || "");
  const adMode = typeof window !== "undefined" ? Boolean(parseURLParams().adMode) : false;
  const onRelease = releaseRoute !== null;

  const eligible = useMemo(() => {
    if (!isConvexConfigured || !settings || !settings.enabled) return false;
    if (hiddenRoute) return false;
    if (isGatedArtistPath(location.pathname)) return false;
    if (onRelease && (adMode || !settings.showOnReleasePages)) return false;
    if (getSubscribedEmail()) return false;
    if (wasPopupShownThisSession()) return false;
    const dismissedAt = getPopupDismissedAt();
    if (dismissedAt && Date.now() - dismissedAt < settings.frequencyDays * DAY_MS) return false;
    return true;
  }, [settings, hiddenRoute, onRelease, adMode, location.pathname, location.search]);

  useEffect(() => {
    if (hiddenRoute) return;
    let cancelled = false;
    fetchCachedGetApi<ArtistLookup>("/.netlify/functions/SpotifyGetArtist", { artist: artistSlug }, {
      ttlMs: API_CACHE_TTL.SIX_HOURS,
      shouldCache: (data) => Boolean(data?.artistData),
    })
      .then((data) => {
        if (cancelled || !data?.artistData?.name) return;
        setArtist({ name: data.artistData.name, image: data.artistData.images?.[0]?.url });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [artistSlug, hiddenRoute]);

  const show = useCallback(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    rememberPopupShownThisSession();
    setOpen(true);
    track("popup_shown", { eventCategory: "mailing", artist: artistSlug, metadata: { pagePath: location.pathname } });
  }, [artistSlug, location.pathname, track]);

  useEffect(() => {
    if (!eligible || !settings || shownRef.current) return;
    const timer = window.setTimeout(show, settings.delayMs);
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = (window.scrollY / scrollable) * 100;
      if (percent >= settings.scrollPercent) show();
    };
    const onLeave = (event: MouseEvent) => {
      if (event.clientY <= 0 && !reallyIsMobile) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [eligible, settings, show, reallyIsMobile]);

  useEffect(() => {
    const onOpenRequest = () => {
      if (!settings) return;
      shownRef.current = true;
      rememberPopupShownThisSession();
      setPhase("form");
      setOpen(true);
      track("popup_opened", { eventCategory: "mailing", artist: artistSlug, metadata: { pagePath: location.pathname, trigger: "footer" } });
    };
    window.addEventListener(MAILING_POPUP_OPEN_EVENT, onOpenRequest);
    return () => window.removeEventListener(MAILING_POPUP_OPEN_EVENT, onOpenRequest);
  }, [artistSlug, location.pathname, settings, track]);

  const handleClose = useCallback(() => {
    trackClick(phase === "form" ? "mailing-popup-dismiss" : "mailing-popup-close", { label: "Mailing popup close", section: "mailing-popup", artist: artistSlug });
    setOpen(false);
    if (phase === "form") {
      rememberPopupDismissed();
      track("popup_dismissed", { eventCategory: "mailing", artist: artistSlug });
    }
  }, [artistSlug, phase, track]);

  if (!settings || !open) return null;

  const covers = (latestPacks ?? []).map((pack) => pack.cover).filter((cover): cover is string => Boolean(cover));
  const artistImage = artist?.image ? getOptimizedImageUrl(artist.image, { width: 640, height: 640, fit: "cover" }) : undefined;
  const packCount = downloadCounts ? (downloadCounts.release?.count ?? downloadCounts.catalog) : 0;
  const packCountLabel = downloadCounts?.release ? `${fileCountLabel(packCount)} on this release` : `${packCount} free downloads live`;
  const artistName = artist?.name || "Truevined";

  const surfaceSx = {
    backgroundColor: "var(--backgroundColor, #141416)",
    color: "var(--textColor, #f4efe6)",
    border: "1px solid color-mix(in srgb, var(--textColor, #f4efe6) 16%, transparent)",
    boxShadow: "0 1.5rem 4rem rgba(0,0,0,0.5)",
  };

  return (
    <BaseModal
      isOpen={open}
      onClose={handleClose}
      desktopModalMobileDrawer
      noPadding
      drawerBottomOffset="0px"
      drawerMaxHeight="min(92dvh, 760px)"
      bodySx={{
        ...surfaceSx,
        width: reallyIsMobile ? "100%" : "min(92vw, 880px)",
        borderRadius: reallyIsMobile ? "22px 22px 0 0" : "20px",
        overflow: "hidden",
      }}
      paperSx={reallyIsMobile ? undefined : { maxWidth: "none", margin: "16px" }}
    >
      <Box
        role="dialog"
        aria-labelledby="mailing-popup-title"
        aria-describedby="mailing-popup-body"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.9fr) minmax(0, 1.1fr)" },
          minHeight: { md: "440px" },
          "& > *": { minWidth: 0 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: "color-mix(in srgb, var(--logoColor, #f4efe6) 10%, var(--backgroundColor, #141416))",
            height: { xs: "200px", md: "auto" },
            borderBottom: { xs: "1px solid color-mix(in srgb, var(--textColor, #f4efe6) 12%, transparent)", md: 0 },
            borderRight: { md: "1px solid color-mix(in srgb, var(--textColor, #f4efe6) 12%, transparent)" },
          }}
        >
          <PackFan covers={covers} artistImage={artistImage} />
          {packCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: "16px",
                bottom: "14px",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                px: "12px",
                py: "6px",
                borderRadius: "999px",
                backgroundColor: "color-mix(in srgb, var(--backgroundColor, #141416) 82%, transparent)",
                backdropFilter: "blur(10px)",
                fontFamily: "Poppins",
                fontSize: { xs: "13px", md: "0.75rem" },
                fontWeight: 600,
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
                maxWidth: "calc(100% - 32px)",
              }}
            >
              <Sparkles style={{ width: "1em", height: "1em", flexShrink: 0 }} aria-hidden />
              {packCountLabel}
            </Box>
          )}
        </Box>
        <Box sx={{ p: { xs: "22px 22px 28px", md: "40px 40px 36px" }, display: "flex", flexDirection: "column", justifyContent: "center", gap: { xs: "14px", md: "16px" } }}>
          <Typography
            component="p"
            sx={{
              fontFamily: "Poppins",
              fontSize: { xs: "13px", md: "0.72rem" },
              fontWeight: 700,
              letterSpacing: "0.26em",
              textTransform: "uppercase",
              color: "var(--logoColor, #c9a84c)",
              m: 0,
            }}
          >
            {artistName} drops
          </Typography>
          {phase === "form" ? (
            <>
              <Typography
                id="mailing-popup-title"
                component="h2"
                sx={{
                  fontFamily: "Hanson, Poppins, sans-serif",
                  fontSize: { xs: "26px", md: "1.9rem" },
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  textWrap: "balance",
                  m: 0,
                }}
              >
                {settings.headline}
              </Typography>
              <Typography
                id="mailing-popup-body"
                sx={{ fontFamily: "Poppins", fontSize: { xs: "16px", md: "0.95rem" }, lineHeight: 1.55, opacity: 0.86, m: 0, maxWidth: "46ch" }}
              >
                {settings.body}
              </Typography>
              <SubscribeForm
                source="popup"
                artist={artistSlug}
                buttonLabel={settings.buttonLabel}
                accentColor="var(--logoColor, #c9a84c)"
                inkColor="var(--backgroundColor, #141416)"
                autoFocus={!reallyIsMobile}
                onSubscribed={(outcome) => {
                  const described = describeSubscribeOutcome(outcome);
                  setOutcomeConfirmed(described.phase === "unlocked");
                  setOutcomeMessage(described.phase === "unlocked" ? `${outcome.email} is already on the list. Nothing else to do.` : described.message);
                  setPhase("done");
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: "Poppins", fontSize: { xs: "13px", md: "0.74rem" }, opacity: 0.6, m: 0 }}>
                  One email per release. Unsubscribe in one tap.
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={handleClose}
                  sx={{
                    background: "none",
                    border: 0,
                    color: "inherit",
                    fontFamily: "Poppins",
                    fontSize: { xs: "14px", md: "0.8rem" },
                    fontWeight: 600,
                    opacity: 0.7,
                    cursor: "pointer",
                    px: "10px",
                    py: "8px",
                    borderRadius: "999px",
                    minHeight: "44px",
                    transition: "opacity 150ms ease",
                    "&:hover": { opacity: 1 },
                    "&:focus-visible": { outline: "2px solid currentColor", outlineOffset: "2px", opacity: 1 },
                  }}
                >
                  Not now
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography
                id="mailing-popup-title"
                component="h2"
                sx={{ fontFamily: "Hanson, Poppins, sans-serif", fontSize: { xs: "26px", md: "1.9rem" }, lineHeight: 1.1, m: 0, textWrap: "balance" }}
              >
                {outcomeConfirmed ? "You are already in" : "Check your inbox"}
              </Typography>
              <Typography id="mailing-popup-body" sx={{ fontFamily: "Poppins", fontSize: { xs: "16px", md: "0.95rem" }, lineHeight: 1.55, opacity: 0.86, m: 0, maxWidth: "46ch" }}>
                {outcomeMessage ?? settings.successMessage}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={handleClose}
                sx={{
                  alignSelf: "flex-start",
                  minHeight: { xs: "52px", md: "46px" },
                  px: "22px",
                  borderRadius: "999px",
                  border: "1px solid color-mix(in srgb, var(--textColor, #f4efe6) 40%, transparent)",
                  background: "none",
                  color: "inherit",
                  fontFamily: "Poppins",
                  fontWeight: 700,
                  fontSize: { xs: "16px", md: "0.9rem" },
                  cursor: "pointer",
                  transition: "background-color 160ms ease, transform 140ms cubic-bezier(0.23, 1, 0.32, 1)",
                  "&:hover": { backgroundColor: "color-mix(in srgb, var(--textColor, #f4efe6) 10%, transparent)" },
                  "&:active": { transform: "scale(0.97)" },
                  "&:focus-visible": { outline: "2px solid currentColor", outlineOffset: "3px" },
                }}
              >
                Back to the music
              </Box>
            </>
          )}
        </Box>
      </Box>
    </BaseModal>
  );
}

export default MailingListPopup;
